import { useMemo, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { REPORT_DATA_PAGE_SIZE, useReportData } from "@/profitability/query";
import { ObjectTableBlock } from "@/profitability/records/object-table-block";
import {
  ReportMetadata,
  useReportPresentation,
} from "@/profitability/report-presentation";
import type {
  ReportBlock as Block,
  ReportControl,
  ReportControlValues,
  ReportDataset,
  ReportSort,
} from "@/profitability/types";
import { formatReportRange, reportError } from "@/profitability/utils";
import type { BentoPlacement } from "./bento-layout";
import { BlockSkeleton } from "./block-skeleton";
import { ChartControls } from "./chart-controls";
import { orderedPoints, toppedPoints } from "./chart-points";
import { InlineFigure, ReportCard } from "./report-card";
import { ReportChart } from "./report-chart";
import { ReportFigures } from "./report-figures";
import { ReportTable } from "./report-table";
import { useTableRows } from "./use-table-rows";
import {
  AGGREGATE_LABEL,
  blockQuery,
  columnLabel,
  groupLabel,
  measureLabel,
  reportGroups,
} from "./utils";

export const ReportBlock = ({
  businessId,
  runId,
  block,
  dataset,
  controls,
  values,
  placement,
  headline,
  fill,
  lens = null,
  variant = "card",
}: {
  businessId: string;
  runId: string;
  block: Block;
  dataset: ReportDataset;
  controls: ReportControl[];
  values: ReportControlValues;
  placement: BentoPlacement;
  headline?: string[];
  fill?: boolean;
  lens?: { start_date: string; end_date: string } | null;
  /** "inline" drops the card, for a figure sharing the supporting strip. */
  variant?: "card" | "inline";
}) => {
  const classic = useReportPresentation() === "classic";
  const currency = useReportCurrency();
  const chart =
    block.type === "bar" || block.type === "line" || block.type === "pie";
  // A chart opens in the order and breakdown the script declared, and the
  // reader can change both without touching the saved result.
  const [breakdown, setBreakdown] = useState(chart ? block.x : undefined);
  const [sort, setSort] = useState<ReportSort>(
    (chart && block.sort) ||
      (block.type === "line"
        ? { by: "label", direction: "asc" }
        : { by: "value", direction: "desc" }),
  );
  const table = block.type === "table";
  const query = useMemo(
    () =>
      blockQuery(
        block,
        controls,
        values,
        breakdown,
        lens && dataset.period_field
          ? { field: dataset.period_field, ...lens }
          : null,
      ),
    [block, controls, values, breakdown, lens, dataset.period_field],
  );
  // A chart with a limit, in order of its value, leaves the order and the
  // limit to the API: it groups once and sends the top groups, rather than
  // every group a page at a time for the chart to drop all but a few. In
  // order of its label the chart still reads every group, since the API
  // cannot sort labels the way the page does.
  const dataQuery = useMemo(
    () =>
      chart &&
      block.limit &&
      block.limit <= REPORT_DATA_PAGE_SIZE &&
      sort.by === "value" &&
      query.aggregates
        ? {
            ...query,
            order: { field: query.aggregates[0].as, direction: sort.direction },
            top: block.limit,
          }
        : query,
    [chart, block, sort, query],
  );
  // Only figures and charts ask the API; a table's rows came with the output.
  const data = useReportData(businessId, runId, dataQuery, {
    enabled: !table,
  });
  // Set when the API applied the limit: the rows are then the top groups only.
  const topped = data.data?.total_groups !== undefined ? data.data : undefined;
  const { tableRows, isObject } = useTableRows(block, dataset, query.filters);
  const field =
    block.type === "table"
      ? undefined
      : dataset.columns.find(
          (column) =>
            column.key === (block.type === "summary" ? block.field : block.y),
        )!;
  const valueColumn = field && {
    ...field,
    type:
      block.type !== "table" && block.aggregate === "count"
        ? ("number" as const)
        : field.type,
  };
  const counted = block.type !== "table" && block.aggregate === "count";
  const named =
    block.title ??
    block.label ??
    (field
      ? `${columnLabel(field)}${counted && !classic ? " count" : ""}`
      : block.id.replaceAll("_", " "));
  // A heading starts with a capital, whatever case the script wrote it in.
  const title = named.charAt(0).toUpperCase() + named.slice(1);
  // One row is its own total, and a count is what its title already says.
  const aggregate =
    block.type === "table" ||
    (!classic && (dataset.rows.length === 1 || counted))
      ? undefined
      : AGGREGATE_LABEL[block.aggregate];
  const rows = table ? tableRows : (data.data?.rows ?? []);
  const resultField = query.aggregates?.[0].as ?? "value";
  const groups = reportGroups(controls, values, dataset.id);
  const body = () => {
    // Only before the first rows arrive. Changing a filter refetches every
    // block on the page, and blanking them all out to redraw the same shapes
    // reads as the page breaking rather than as it answering.
    if (!table && data.isPending)
      return <BlockSkeleton type={block.type} compact={variant === "inline"} />;
    if (!table && data.isError)
      return (
        <Stack gap={2} align="start" className="px-4 pb-4">
          <Text variant="caption" role="alert" tone="destructive">
            {reportError(data.error)}
          </Text>
          <Button variant="secondary" onClick={() => void data.refetch()}>
            Retry results
          </Button>
        </Stack>
      );
    if (!rows.length)
      return (
        <Empty
          className={emptyStateVariants({
            layout: block.type === "summary" ? "inline" : "collection",
            className: "px-4",
          })}
        >
          <EmptyHeader>
            <EmptyDescription>
              No results for this view. Change or reset the filters.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      );
    if (block.type === "table" && isObject && !groups.length)
      return (
        <ObjectTableBlock
          key={JSON.stringify(query)}
          {...{ block, dataset, rows, headline }}
          label={title}
        />
      );
    if (block.type === "table")
      return (
        <ReportTable
          key={JSON.stringify(query) + JSON.stringify(groups)}
          rows={rows}
          fields={block.columns}
          groups={groups}
          dataset={dataset}
          detail={block.detail}
          headline={headline}
          fill={fill}
          defaultSort={block.sort}
        />
      );
    if (block.type === "summary")
      return (
        <ReportFigures
          rows={rows}
          groups={query.group_by}
          dataset={dataset}
          column={valueColumn!}
          field={resultField}
          compact={variant === "inline"}
        />
      );
    const points = rows.map((row) => ({
      label:
        labelColumn?.type === "date"
          ? String(row[labelColumn.key] ?? "")
          : groupLabel(row, query.group_by, dataset, currency),
      value: row[resultField] == null ? null : Number(row[resultField]),
      sort: row[query.group_by[0]],
    }));
    const shown = topped
      ? toppedPoints(points, sort, topped.other)
      : orderedPoints(points, sort, block.limit, block.aggregate);
    return (
      <ReportChart
        type={block.type}
        column={valueColumn!}
        measure={measureLabel(valueColumn!, block.label)}
        labelColumn={labelColumn}
        narrow={placement.narrow}
        rows={shown}
      />
    );
  };
  // What names each point is what the rows were grouped by. That is the
  // breakdown, unless a group control is on: then the groups replace it, and
  // a chart drawn by date but grouped by technician names technicians.
  const grouping = chart ? query.group_by : [];
  const labelColumn =
    grouping.length === 1
      ? dataset.columns.find((column) => column.key === grouping[0])
      : undefined;
  // A chart broken down by date says the span it covers, since its axis now
  // reads as days of the week.
  // The span of every group, not only the top ones the API sent.
  const dates =
    labelColumn?.type !== "date"
      ? []
      : topped
        ? (topped.group_range ?? [])
        : rows
            .map((row) => String(row[labelColumn.key] ?? ""))
            .filter(Boolean)
            .sort();
  // A chart regrouped from the one the script declared says so, since its
  // title still names the breakdown it was declared with.
  const regrouped =
    chart && grouping.length && (grouping.length > 1 || grouping[0] !== block.x)
      ? `By ${grouping
          .map((key) =>
            columnLabel(dataset.columns.find((column) => column.key === key)!),
          )
          .join(" and ")}`
      : undefined;
  const span = dates.length
    ? formatReportRange(dates[0], dates[dates.length - 1])
    : undefined;
  const subtitleParts = [regrouped, span].filter(
    (part): part is string => !!part,
  );
  const subtitle = subtitleParts.length ? (
    <ReportMetadata>{subtitleParts}</ReportMetadata>
  ) : undefined;
  // The column the chart is drawn by, then the others it offers. x_options is
  // documented as the OTHER columns a reader may regroup by, so a chart whose
  // x is not among them is correct — and used to leave the control with no
  // column for the value it was showing, which took the whole page down.
  const breakdowns = (chart ? [block.x, ...(block.x_options ?? [])] : [])
    .filter((key, index, keys) => keys.indexOf(key) === index)
    .map((key) => dataset.columns.find((column) => column.key === key))
    .filter((column) => !!column);
  if (variant === "inline")
    return (
      <InlineFigure title={title} className={placement.span}>
        {body()}
      </InlineFigure>
    );
  return (
    <ReportCard
      title={title}
      subtitle={subtitle}
      bare={table}
      actions={
        // A group control overrides the breakdown, so while one is on the
        // picker would change nothing and is left out. The order still
        // applies to the grouped points, so it stays, said in the terms of
        // whatever names them now.
        chart && breakdown ? (
          <ChartControls
            breakdowns={breakdowns}
            regroupable={!groups.length}
            labelColumn={labelColumn}
            field={breakdown}
            onFieldChange={setBreakdown}
            sort={sort}
            onSortChange={setSort}
          />
        ) : undefined
      }
      note={
        aggregate && !title.toLowerCase().includes(aggregate.toLowerCase())
          ? aggregate
          : undefined
      }
      className={cn(placement.span, placement.height)}
    >
      {body()}
    </ReportCard>
  );
};
